/* eslint-disable react/jsx-props-no-spreading */
import { Button, Tag } from '@blueprintjs/core';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { Text } from 'vet-bones/components';
import { MimeType } from 'vet-bones/constants';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { FileExtension } from 'src/constants/fileExtensions';
import { ProjectFileUpload } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { validateHeaders } from 'src/utils/csv/validateHeaders';
import { ExportToFileOptions } from 'src/utils/exportToFile';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { readFile } from 'src/utils/readFile';

type FileUploadInputProps = {
  fileOptions: ExportToFileOptions | null;
  fileUpload: ProjectFileUpload;
  setFileOptions: (fileOptions: ExportToFileOptions | null) => void;
};

export const FileUploadInput: React.VFC<FileUploadInputProps> = ({
  fileOptions,
  fileUpload,
  setFileOptions,
}) => {
  const dispatch = useReduxDispatch();

  const onDropAccepted = React.useCallback(
    async (files: File[]) => {
      const [file] = files;
      const contents = await readFile(file);
      const areHeadersSame = await validateHeaders(fileUpload, contents);

      if (!areHeadersSame) {
        dispatch(
          projectsActionCreators.uploadedMismatchedHeadersCsv(file.name)
        );
        setFileOptions(null);
        return;
      }

      setFileOptions({
        contents,
        filename: file.name,
        mimeType: MimeType.CSV,
      });
    },
    [dispatch, fileUpload, setFileOptions]
  );

  const { getInputProps, getRootProps, open } = useDropzone({
    accept: FileExtension.CSV,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDropAccepted,
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {!fileOptions ? (
        <Button onClick={open} text={copy.components.dropzone.browse} />
      ) : (
        <Tag interactive minimal onRemove={() => setFileOptions(null)}>
          <Text ellipsize tagName="i">
            {fileOptions.filename}
          </Text>
        </Tag>
      )}
    </div>
  );
};
